import type { ClassValue } from "clsx";

import { clsx } from "clsx";
import { debounce } from "lodash";
import { twMerge } from "tailwind-merge";
import { v4 as uuidv4, v7 as uuidv7 } from "uuid";

import { splitWordsOnUpperCase } from "./format";

/** Not null or undefined */
export function hasValue(i: any) {
	return i !== undefined && i !== null;
}

/** Returns true if the value is an empty string */
export function isEmpty(i: any) {
	if (i instanceof Date) return false;
	if (typeof i === "string") return i.trim() === "";
	if (Array.isArray(i)) return i.length === 0;
	if (typeof i === "object") return Object.keys(i).length === 0;
	return i === "";
}
export function noValue(i: any) {
	return !hasValue(i) || isEmpty(i);
}
export function notEmpty(i: any) {
	return hasValue(i) && !isEmpty(i);
}

/** Returns true if either string contains the other, case insensitive */
export function bidirectionalStringContains(string1: string, string2: string, caseInsensitive = true) {
	if (caseInsensitive) {
		string1 = string1.toLowerCase();
		string2 = string2.toLowerCase();
	}
	return string1.includes(string2) || string2.includes(string1);
}

/** Concatenates css styles */
export function styles(...classNames: string[]) {
	return classNames.filter((s) => s).join(" ");
}

/** Get URL param value from name of url param */
export function getUrlParameter(name: string) {
	return new URLSearchParams(window.location.search).get(name) ?? "";
}

/** Creates deep copy of object */
export function clone<T>(obj: T) {
	return obj && (JSON.parse(JSON.stringify(obj)) as T);
}

/** Local midnight of the current day */
export function today() {
	const now = new Date();
	return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/** Generates guid
 * https://learn.microsoft.com/en-us/dotnet/api/system.guid.newguid?view=net-10.0
 * */
export function guid() {
	return uuidv4();
}

/** Generates guid based on current time
 * https://learn.microsoft.com/en-us/dotnet/api/system.guid.createversion7?view=net-10.0
 * */
export function guidTimeBased() {
	return uuidv7();
}

const UPPER_CASE_MATCH = /([A-Z])/g;
const FIRST_LETTER_MATCH = /^./;
export function enumToString(e: string) {
	return e
		.replace(UPPER_CASE_MATCH, (match) => ` ${match}`)
		.replace(FIRST_LETTER_MATCH, (match) => match.toUpperCase())
		.trim();
}

export function enumToOptions(enumObj: Record<string, unknown>) {
	return Object.keys(enumObj)
		.filter((k) => isNaN(Number(k)))
		.map((k) => ({ label: splitWordsOnUpperCase(k), value: enumObj[k] as number }));
}

/** Delays for a given number of milliseconds */
export function delay(ms: number) {
	return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

export interface Surefox {
	closeCurrentTab: Function;
	closeAllTabs: Function;
	back: Function;
	forward: Function;
	home: Function;
	refresh: Function;
	stop: Function;
	exit: Function;
	isAppInstalled: (appId: string) => boolean;
	killApp: (appId: string) => boolean;
	load: (url: string) => void;
	openInNewTab: (url: string) => void;
	launch: (app: string, restart: boolean) => void;
}
interface HostWindow {
	surefox: Surefox;
	opera?: string;
	MSStream?: unknown;
}

const hostWindow = () => window as unknown as HostWindow;

export function getSurefox() {
	return hostWindow().surefox;
}

function isIos() {
	const userAgent = navigator.userAgent || navigator.vendor || hostWindow().opera;
	return /iPad|iPhone|iPod/.test(userAgent) && !hostWindow().MSStream;
}

/** Borrowed from AVL to try to prevent scroll to refresh */
export function preventBounce() {
	if (isIos()) {
		//need to add event after body created
		document.body.addEventListener(
			"touchmove",
			function (e: TouchEvent) {
				e.preventDefault();
				//magically helps click events to be more precise
				if (e.target) {
					debounce(function () {
						(e.target as HTMLElement).click();
					}, 300)();
				}
			},
			false,
		);
	}
}

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}
